/**
 * MIRROR of `server/helpers/money.js` — read the reasoning there.
 *
 * 🔑 **A DISPLAY CONVENIENCE, and NEVER a gate.** The server rounds every
 * stored money figure itself; nothing here decides what is persisted. This
 * exists so `utils/hireCost.js`'s four rounding call sites — behind the order
 * form's live "Line Total" and the Hires Gantt's drag preview — reach the SAME
 * rounding rule the server will apply, instead of a second copy of it.
 *
 * 🪞 **WHY THIS FILE EXISTS AT ALL, and it is not a nicety.** When the server's
 * `round2` was made to delegate to `money.js` (2026-09-08, money.js T1), the
 * client's `round2` had to delegate identically or
 * `server/tests/regressions/hire-cost-client-parity.test.js` would go red: that
 * ratchet compares the two hireCost files as SOURCE TEXT over the span
 * `const MS_PER_DAY` … end of `orderLineTotal`, and `round2` sat inside it.
 * The implementation plan recorded the client mirror of money.js as an
 * unscheduled gap that was not needed; that was measured wrong before any code
 * moved. 🔻 **T7 then DELETED `round2` from both tiers, again in one commit and
 * for the same reason** — the span now holds four `roundMoney(toHireNumber(…))`
 * call sites instead, so this module is reached MORE often, not less. Held in
 * step by `server/tests/regressions/money-client-parity.test.js`.
 *
 * ── WHAT IS MIRRORED, AND WHAT IS DELIBERATELY NOT ────────────────────
 * Only the rounding rule: `HALF_PENNY_UP`, `toMoneyNumber`, `roundMoney`. The
 * server module also exports `toPence`, `fromPence`, `sumMoney`, `mulMoney` and
 * `pctOf`; **no client caller uses them and they are deliberately absent** — an
 * unused mirrored function is dead code that drifts silently, and the parity
 * test compares only the span that exists on both sides. If a client caller
 * ever needs one, copy it VERBATIM from the server file and widen the parity
 * span in the same commit.
 *
 * 🔴 **THE RULE ITSELF IS THE SERVER'S DECISION, restated here so nobody reads
 * the flag's name as its behaviour.** `HALF_PENNY_UP = true` is Rule A: plain
 * 2dp `Math.round` PLUS a ULP-scale `+ Number.EPSILON` nudge. It is NOT a
 * decimal half-up rule — `4 + Number.EPSILON === 4`, so above £4 it IS plain
 * `Math.round`, and 4,573 of the 100,000 decimal half-pennies in £0.005–£999.995
 * still round DOWN under it. An exact half rounds up under both rules, so "an
 * exact half-penny goes up" describes neither. Flipping it is an owner decision
 * and a SERVER change; this file follows.
 *
 * 🪤 `roundMoney(-0.001)` is `-0`, exactly as the server's is, and
 * `Object.is(-0, 0)` is FALSE — a test using `toBe` on a negative near-zero
 * must expect `-0`. Not corrected here: correcting it would diverge from the
 * rule this file exists to BE.
 */
const HALF_PENNY_UP = true;

/** Radix literals only. Scientific notation is deliberately NOT matched. */
const RADIX_LITERAL = /^[+-]?0[xXoObB]/;

export function toMoneyNumber(v) {
  if (v === null || v === undefined || v === '') return null;
  const t = typeof v;
  // `Number(symbol)` THROWS; every other rejected type merely coerces wrongly.
  if (t === 'object' || t === 'function' || t === 'symbol' || t === 'boolean') return null;
  if (t === 'string' && RADIX_LITERAL.test(v.trim())) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function roundMoney(v) {
  const n = toMoneyNumber(v);
  if (n === null) return null;
  const r = HALF_PENNY_UP
    ? Math.round((n + Number.EPSILON) * 100) / 100
    : Math.round(n * 100) / 100;
  // A finite input can still overflow: 1e308 * 100 is Infinity, and Math.round
  // of Infinity is Infinity. Money that is not a finite number is not money.
  return Number.isFinite(r) ? r : null;
}

export { HALF_PENNY_UP };
